/* Completezza dei dati scatola di un articolo venduto a mq/ml.
   Senza mq/scatola (o ml/confezione) e pz/scatola il preventivo non è
   trasformabile in ordine: le scatole da ordinare e i pezzi da movimentare
   non sono derivabili dalla quantità in mq. L'operatore può comunque
   preventivare, ma la scheda va completata prima di confermare al cliente. */

import { measureGroupOf } from "./quoteItemUnits";

const COLUMN_GROUPS = {
  area: [
    "piastrelle",
    "rivestimenti parete",
    "parquet",
    "laminati",
    "decking esterno",
  ],
  piece: ["sanitari", "rubinetteria", "box doccia", "accessori bagno"],
  linear: ["profili", "sistemi di scarico"],
  furniture: ["mobili", "porte e infissi"],
};

/* Come nelle pagine dei preventivi: decide il dato dell'articolo, la categoria
   è l'ultima risorsa. */
export function itemCategoryGroup(item) {
  const fromData = measureGroupOf(item);
  if (fromData) return fromData;
  const category = typeof item === "string" ? item : item?.category;
  for (const [group, cats] of Object.entries(COLUMN_GROUPS)) {
    if (cats.includes(category)) return group;
  }
  return "generic";
}

const isPositive = (v) => Number(v) > 0;

/* mq/ml effettivi per scatola dell'articolo: 0 se l'articolo non è venduto a
   scatola o se il dato manca. Con 0 le scatole non sono calcolabili, quindi
   giacenza e quantità da ordinare non vanno mostrate. */
export function itemPerBox(item) {
  const group = itemCategoryGroup(item);
  if (group === "linear") return Number(item?.mlPerBox) || 0;
  if (group === "area") return Number(item?.sqmPerBox) || 0;
  return 0;
}

/* Etichette dei campi per scatola, come compaiono nella scheda articolo. */
export const BOX_FIELD_LABELS = {
  sqmPerBox: "M² / scatola",
  mlPerBox: "ML / confezione",
  piecesPerBox: "Pz / scatola",
};

/* Dati per scatola che un articolo deve avere per essere ordinabile.
   È la stessa lista che la scheda articolo deve offrire: se le due divergono,
   la conferma pretende un dato che il form non lascia inserire — è successo
   con un piatto doccia venduto a mq in categoria «bagno». */
export function boxFieldsForItem(item) {
  const group = itemCategoryGroup(item);
  if (group === "area") return ["sqmPerBox", "piecesPerBox"];
  if (group === "linear") return ["mlPerBox", "piecesPerBox"];
  return [];
}

/* Campi scatola mancanti, con le etichette del form articolo.
   N° scatole è la giacenza: si segnala ma non blocca la conferma. */
export function missingStockFields(item) {
  // un campo assente dalla risposta API (undefined) non è un campo vuoto:
  // si giudica solo quello che è stato effettivamente caricato
  const known = (v) => v !== undefined;

  const missing = [];
  for (const field of boxFieldsForItem(item)) {
    if (known(item?.[field]) && !isPositive(item?.[field])) {
      missing.push(BOX_FIELD_LABELS[field]);
    }
  }
  // N° scatole vive sul magazzino (warehouse.boxQuantity), non sull'articolo:
  // se il magazzino non è stato caricato il dato non si può giudicare
  const boxes = item?.warehouse?.boxQuantity ?? item?.boxQuantity;
  if (known(boxes) && !isPositive(boxes))
    missing.push("N° scatole (da Magazzino)");
  return missing;
}

/* True se manca un dato strutturale (mq/ml per scatola o pz/scatola): il
   preventivo resta salvabile ma non confermabile. */
export function hasIncompleteStockData(item) {
  const missing = missingStockFields(item);
  return missing.some((f) => !f.startsWith("N° scatole"));
}

/* Etichetta dell'articolo da mostrare negli avvisi */
export function itemStockLabel(item) {
  return (
    item?.itemName ||
    item?.subBrand ||
    item?.brand ||
    item?.supplierCode ||
    item?.itemCode ||
    "—"
  );
}
